import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import PortfolioCreateDialogFields from "../../field/portfolio/PortfolioCreateDialogFields";
import {HttpRestResponse} from "@softvision/webpdf-wsclient-9-typescript/lib/http/HttpRestResponse";
import FileHelper from "../../../../shared/helper/FileHelper";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	Functions,
	Information,
	PortfolioFile,
	PortfolioFileInterface,
	Settings,
	SettingsInterface,
	SignatureEditProfile
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {PortalToolboxPortfolioPortfolio} from "../../../../shared/models/PortalToolboxPortfolioPortfolio";
import {PortalPortfolioAdd} from "../../../../shared/models/PortalPortfolioAdd";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

export class PortfolioCreateDialogDefinition extends PortalDialog<PortalToolboxPortfolioPortfolio,
	PortalDialogProperties<PortalToolboxPortfolioPortfolio>,
	PortalDialogState<PortalToolboxPortfolioPortfolio>> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			fieldErrors: new Map<PortfolioCreateDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.portfolio, Functions.getPortfolioDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		for (let selectedDocument of selectedDocuments) {
			if (selectedDocument.isLocked()) {
				return false;
			}
		}

		return selectedDocuments.length > 0;
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxPortfolioPortfolio | undefined = this.state.parameter?.data;
		let errors: Map<PortfolioCreateDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter?.add)) {
			return null;
		}

		let addParameter: PortalPortfolioAdd = parameter!.add as PortalPortfolioAdd;

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-filename">
						<Translate {...this.props} data="portfolio.create.filename.label" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8">
						<div className={"p-inputgroup" + (
							errors.has(PortfolioCreateDialogFields.FILENAME) ? " p-invalid" : ""
						)}>
							<InputText id="control-filename" autoFocus={true}
									   className={errors.has(PortfolioCreateDialogFields.FILENAME) ? "p-invalid" : ""}
									   tooltip={errors.has(PortfolioCreateDialogFields.FILENAME) ? Translate.value(
										   errors.get(PortfolioCreateDialogFields.FILENAME)!, {ns: "message"}
									   ) : ""}
									   value={FunctionHelper.applyValue(
										   addParameter?.fileName, PortalPortfolioAdd.getFileNameDefault()
									   )}
									   onInput={(e) => this.onChange(
										   PortfolioCreateDialogFields.FILENAME, e.currentTarget.value
									   )}/>
							<span className="p-inputgroup-addon">.pdf</span>
						</div>
						{this.renderFieldError(errors.has(PortfolioCreateDialogFields.FILENAME) ?
							Translate.value(
								errors.get(PortfolioCreateDialogFields.FILENAME)!, {ns: "message"}
							) : ""
						)}
					</div>
				</fieldset>
			</>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["portfolio-create-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()}
					appendTo={document.getElementById("root")} resizable={false}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.portfolio.create.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected onChange(field: PortfolioCreateDialogFields, value: any): void {
		let tempParameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data;
		let addParameter: PortalPortfolioAdd = tempParameter.add as PortalPortfolioAdd;
		let errors: Map<PortfolioCreateDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case PortfolioCreateDialogFields.FILENAME:
				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!FileHelper.isFilename(value)) {
					errors.set(field, "errors.fields.filename");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				addParameter.fileName = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-portfolio small"}/>
					<Translate {...this.props} data="portfolio.create.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxPortfolioPortfolio | undefined = this.state.parameter?.data;

		if (!FunctionHelper.exists(parameter?.add)) {
			return;
		}

		let addParameter: PortalPortfolioAdd = parameter!.add as PortalPortfolioAdd;
		if (addParameter.fileName === "") {
			addParameter.fileName = Translate.value("portfolio.create.filename.default", {ns: "dialog"});
		}

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone()
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		let parameter: PortalToolboxPortfolioPortfolio = this.state.parameter!.data.clone();
		let addParameter: PortalPortfolioAdd = parameter.add as PortalPortfolioAdd;

		// clear old file data
		addParameter.file = [];

		for (let portalDocument of selection) {
			try {
				let response: HttpRestResponse = await documentManager.downloadDocument(
					portalDocument.getSourceDocumentId()!
				);

				if (!response.successful()) {
					break;
				}

				let data: ArrayBuffer = await response.getData() as ArrayBuffer;

				addParameter.file?.push(PortfolioFile.fromJson({
					path: '/',
					mimeType: portalDocument.getMimeType().toString(),
					fileName: portalDocument.getFileName(),
					data: {
						value: FileHelper.ArrayBufferToBase64(data)
					}
				} as PortfolioFileInterface));

				if (!FunctionHelper.exists(addParameter.initialFileName)) {
					addParameter.initialFileName = portalDocument.getFileName();
				}
			} catch (exception: any) {
				await this.props.context.session.handleException(exception);
				if (await this.onError(exception)) {
					await this.submit();
				}
			}
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			portfolio: parameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;

		try {
			await this.props.context.session.executeRequest({
				webServiceType: webServiceType,
				operation: operationData,
				filename: addParameter.fileName,
				settings: Settings.fromJson({
					pdfa: {
						pdfaEditProfile: this.props.pdfaEditProfile
					},
					signature: {
						signatureEditProfile: this.props.signatureEditProfile
					},
					compress: {
						compressProfile: this.props.compressProfile,
						compressObjects: {
							objectStreamSize: this.props.objectStreamSize
						}
					}
				} as SettingsInterface)
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.toolbox.portfolio.merge", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.hide();
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.PORTFOLIO_CREATE;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxPortfolioPortfolio> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}

	protected isEditPasswordNeeded(): boolean {
		return false; // Don't check for password
	}
}

const PortfolioCreateDialog = React.memo(PortfolioCreateDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxPortfolioPortfolio>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default PortfolioCreateDialog;